import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useEffect, useState, useRef } from "react";
import classNames from "classnames";

interface Option {
  value: string;
  label: string;
}

interface BasicSelectProps {
  id: string;
  className?: string;
  label?: string;
  initialSelectedOption?: string;
  options: Option[];
  onSelect: (option: Option) => void;
}

const BasicSelect: React.FC<BasicSelectProps> = ({
  id,
  className = "",
  label,
  initialSelectedOption,
  options,
  onSelect,
}) => {
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const selectRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!selectedOption && initialSelectedOption) {
      const initialOption = options.find(
        (option) => option.value === initialSelectedOption
      );
      setSelectedOption(initialOption || null);
    }
  }, [initialSelectedOption, options, selectedOption]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectRef]);

  const handleSelectClicked = () => {
    if (isOpen) {
      setIsOpen(false);
      selectRef.current?.blur();
    } else {
      setIsOpen(true);
      selectRef.current?.focus();
    }
  };

  const handleValueChanged = (option: Option) => {
    setSelectedOption(option);
    setIsOpen(false);
    onSelect(option);
  };

  return (
    <div className={`relative ${className}`} ref={selectRef}>
      {label && (
        <label
          htmlFor={id}
          className="block mb-1 text-sm font-medium text-gray-700"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <div className="w-[1px] h-[24px] bg-gray-400 absolute right-[40px] top-[19px] -translate-y-1/2 pointer-events-none" />
        <ChevronDownIcon
          className="h-5 w-auto text-gray-400 absolute right-3 top-[19px] -translate-y-1/2 pointer-events-none"
          aria-hidden="true"
        />
        <div
          id={id}
          className={classNames(
            "block w-full pl-3 pr-[52px] py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm",
            {
              "outline-none ring-1 ring-indigo-500 border-indigo-500": isOpen,
            }
          )}
          tabIndex={0}
          onClick={handleSelectClicked}
        >
          {selectedOption && selectedOption.label}
        </div>
      </div>
      {isOpen && (
        <div className="absolute z-50 w-full left-[-1px] top-[calc(100%+8px)]">
          <div className="w-[calc(100%+2px)] flex flex-col bg-white rounded-md overflow-y-scroll max-h-[234px] py-1 shadow-lg ring-1 ring-black ring-opacity-5">
            {options.map((option) => {
              return (
                <div
                  className={classNames(
                    `relative text-left px-3 py-[9px] text-sm w-full hover:cursor-pointer flex flex-row text-gray-700`,
                    {
                      "bg-indigo-200": selectedOption?.value === option.value,
                    }
                  )}
                  key={option.value}
                  onClick={() => handleValueChanged(option)}
                >
                  <span>{option.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BasicSelect;
